
import Vue from 'vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import RunnitImageTimer from "@/views/Runnits/RunnitImageTimer.vue";
import ListItem from '@/components/base/ListItem.vue';
import { Avatar, MODEL_CREATE_METHOD, RUNNIT_NODE_DEF_TAG_TYPE } from '@run-diffusion/shared';
import { mapState, mapActions, mapGetters } from 'vuex';
import { TRAINING_TYPE_CARDS } from '@/views/Runnits/constants';
import { storageRunnit1 } from '@/firebase';
import { getRunnitModelFilePath } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import _trim from 'lodash/trim';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'ModelSelectCard',
	props: {
		model: { type: Object, required: true },
		disabled: { type: Boolean, default: false },
		selectIcon: { type: String, default: 'mdi-check-circle-outline' },
		selectText: { type: String, default: 'Select' },
		disabledText: { type: String, default: null },
		disabledCtaText: { type: String, default: null },
		disabledCtaProps: { type: Object, default: () => ({}) },
		small: { type: Boolean, default: false },
		noClick: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
		loadingProgress: { type: Number, default: 0 },
		loadAt: { type: Number, default: Date.now() },
		loadingMessage: { type: String, default: null },
		clickableContainerClasses: { type: Object, default: null },
		disableDownload: { type: Boolean, default: false },
	},
	mixins: [TeamUserMixin],
	data () {
		return {
			isDownloadingModel: false,
		};
	},
	computed: {
		...mapState([
			'publicRunnitNodeDefTagsMap',
			'modelsState',
		]),
		...mapGetters([
			'canDownloadModels',
		]),
		showActionBtns (): boolean {
			return !!(
				!this.loading &&
				this.model?.file
			);
		},
		title (): string {
			return this.model?.name || '';
		},
		description (): string {
			return this.model?.description || '';
		},
		avatar (): Avatar {
			return this.model?.avatar || null;
		},
		tags (): string[] {
			const tags: string[] = [];
			Object.keys(this.model?.tags || {})
				.filter((tag: string) => this.publicRunnitNodeDefTagsMap[tag].type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE)
				.forEach((tag: string) => {
					tags.push(TRAINING_TYPE_CARDS[tag].title);
				});
			if (this.model?.architecture) tags.push(this.model.architecture);
			if (this.model?.createMethod && this.model.createMethod === MODEL_CREATE_METHOD.UPLOAD) tags.push('Upload');
			if (this.model?.createMethod && this.model.createMethod === MODEL_CREATE_METHOD.RUNNIT) tags.push('Trained');
			return tags;
		},
		computedContainerWidth (): number {
			return 256;
		},
		computedContainerHeight (): number {
			return 256;
		},
		computedImageHeightWidth (): number {
			return 256;
		},
		computedDescriptionCharLimit (): number {
			const baseLimit: number = this.small ? 60 : 70;
			if (this.title && this.title.length > 18) {
				// Reduce character limit if title is long
				return baseLimit - 15; // Reduce by 15 characters
			}
			return baseLimit;
		},
		cardStyles () {
			let styles = {
				background: 'linear-gradient(180deg, var(--grey-825), var(--grey-835))',
			};

			return styles;
		},
		descriptionStyles () {
			const styles = {
				color: 'rgba(255, 255, 255, 0.6)',
			};

			return styles;
		},
	},
	methods: {
		...mapActions([
			'updateModelState',
			'updateSnackbar',
			'setUpsellDialog',
		]),
		onCardClick () {
			if (this.noClick || this.disabled) return;
			this.$emit('on-select', this.model);
		},
		onEditClick () {
			this.updateModelState({
				modelDraft: this.model,
				modelDialog: {
					...this.modelsState.modelDialog,
					open: true,
					mode: 'edit',
				},
			});
		},
		async onDownloadClick () {
			if (this.isDownloadingModel || this.disableDownload || !this.model?.file?.name) return;

			if (
				this.model.createMethod === MODEL_CREATE_METHOD.UPLOAD ||
				this.canDownloadModels
			) {
				try {
					this.isDownloadingModel = true;

					// Get proper file path from the shared utility function
					const { filePath } = getRunnitModelFilePath(this.model);

					// Get download URL using storage reference
					const fileRef = storageRunnit1.ref(filePath);

					// Get the download URL
					const modelUrl: string = await fileRef.getDownloadURL();

					// Get the model file blob
					const blob: Blob = await (await fetch(modelUrl)).blob();

					// Create zip file
					const zip: JSZip = new JSZip();

					// Extract the file extension from the original name
					const originalName: string = this.model.file.name;
					const extension: string = originalName.substring(originalName.lastIndexOf('.'));

					// Create sanitized filename for the model
					const sanitizedName: string = this.model.name
						.replace(/[^a-zA-Z0-9_-]+/g, '_')
						.replace(/^_+|_+$/g, ''); // Trim leading and trailing underscores

					// Add model file to zip
					zip.file(`${sanitizedName}${extension}`, blob);

					// Add trigger word file if it exists and is not empty
					const trimmedTriggerWord: string = _trim(this.model.triggerWord);
					if (trimmedTriggerWord) {
						zip.file('trigger_word.txt', trimmedTriggerWord);
					}

					// Generate and download the zip file
					zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
						saveAs(content, `${sanitizedName}.zip`);
					});

					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Downloaded model "${this.model.name}"`,
						show: true,
					});
				} catch (e) {
					console.error('Error downloading model:', e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error downloading model`,
						show: true,
					});
				} finally {
					this.isDownloadingModel = false;
				}
			} else {
				this.setUpsellDialog({ runnitCannotDownloadModel: true });
			}
		},
	},
	watch: {
		isDownloadingModel (newVal: boolean) {
			this.$emit('download-status-change', {
				modelId: this.model.id,
				isDownloading: !!newVal,
			});
		},
	},
	components: {
		RunnitImageTimer,
		RunnitImage,
		ListItem,
	},
});
